"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { MapPin, Landmark, CalendarDays, Languages, GraduationCap, Euro, Users } from "lucide-react";
import HoverArrow from "@/components/shared/HoverArrow";
import CriteriaTable from "@/components/unis/CriteriaTable";
import {
  semesterLabel,
  uniDetailPath,
  uniImagePath,
  type University,
} from "@/lib/universities";

function MetaItem({ icon: Icon, children }: { icon: typeof MapPin; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-ink-soft">
      <Icon size={14} className="text-accent shrink-0" aria-hidden="true" />
      {children}
    </span>
  );
}

function PrivateInfo({ university }: { university: University }) {
  return (
    <div className="rounded-xl border border-line bg-paper-deep/60 px-4 py-3.5 flex flex-col gap-2.5">
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
        <Euro size={15} className="text-accent shrink-0" aria-hidden="true" />
        Studiengebühren:
        <span className="text-accent">{university.tuition}</span>
      </span>
      {university.seats && (
        <span className="inline-flex items-center gap-2 text-sm text-ink-soft">
          <Users size={15} className="text-accent shrink-0" aria-hidden="true" />
          {university.seats} Studienplätze pro Jahr
        </span>
      )}
    </div>
  );
}

export default function UniversityCard({
  university,
  index = 0,
}: {
  university: University;
  index?: number;
}) {
  const detailHref = uniDetailPath(university.id);

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: (index % 2) * 0.08 }}
      className="card-flat group h-full flex flex-col gap-5 bg-white/60 p-4 sm:p-5 transition-all duration-300 hover:-translate-y-1"
    >
      {/* Uni-Bild mit Bundesland-Badge */}
      <Link
        href={detailHref}
        className="relative block rounded-xl overflow-hidden aspect-[16/9]"
        tabIndex={-1}
        aria-hidden="true"
      >
        <Image
          src={uniImagePath(university.id)}
          alt={university.name}
          width={750}
          height={874}
          priority={index < 2}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-ink/80 text-paper backdrop-blur-sm px-3 py-1 text-xs font-bold uppercase tracking-wider">
          {university.state}
        </span>
      </Link>

      <div className="flex flex-col gap-5 flex-1 px-2 pb-2 sm:px-2.5 sm:pb-2.5">
        {/* Name + Meta */}
        <div className="flex flex-col gap-3">
          <h3 className="font-display font-bold text-xl leading-[1.15] text-ink group-hover:text-accent transition-colors duration-200">
            <Link href={detailHref}>{university.name}</Link>
          </h3>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <MetaItem icon={MapPin}>{university.city}</MetaItem>
            <MetaItem icon={Landmark}>{university.type}</MetaItem>
            <MetaItem icon={CalendarDays}>{semesterLabel(university.semesters)}</MetaItem>
            <MetaItem icon={Languages}>{university.language}</MetaItem>
            <MetaItem icon={GraduationCap}>
              {university.subjects.length}{" "}
              {university.subjects.length === 1 ? "Studiengang" : "Studiengänge"}
            </MetaItem>
          </div>
        </div>

        {/* Kriterien (staatlich) bzw. Gebühren (privat) */}
        {university.type === "Staatlich" ? (
          <CriteriaTable university={university} />
        ) : (
          <PrivateInfo university={university} />
        )}

        {/* CTA unten angepinnt für gleiche Kartenhöhen */}
        <div className="mt-auto pt-1">
          <Link
            href={detailHref}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent text-paper px-6 py-3 min-h-[46px] text-sm font-semibold hover:bg-accent-deep transition-colors duration-200"
          >
            Mehr Infos
            <HoverArrow />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}
